import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { Section } from "@/components/Section";
import { PageHero } from "@/components/PageHero";
import { MealPlanCard, type MealPlan } from "@/components/MealPlanCard";
import { StepList, type Step } from "@/components/StepList";
import { WhatsAppActions } from "@/components/WhatsAppActions";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { trackCtaClick } from "@/lib/analytics";
import { useMealPlans } from "@/hooks/useMealPlans";
import { usePublicMp2Catalog } from "@/hooks/usePublicMp2Catalog";
import { Badge } from "@/components/ui/badge";
import { SampleDishes } from "@/components/home/SampleDishes";
import { UpcomingMenuPreview } from "@/components/mp2/UpcomingMenuPreview";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { PlanDishStrip } from "@/components/mp2/PlanDishStrip";
import { PlanCard } from "@/components/mp2/PlanCard";
import { AlacarteComingSoon } from "@/components/mp2/AlacarteComingSoon";
import { canonicalUrl } from "@/lib/seo/site";

const META_TITLE = "Mumbai Tiffin & Meal Plans | Poonam Kapur Healthy Kitchen";
const META_DESCRIPTION = "Explore chef-curated meal plans for healthy meal delivery in Mumbai. Browse the weekly menu and contact Poonam Kapur Healthy Kitchen to confirm your area.";

export const Route = createFileRoute("/meal-plans")({
  head: () => ({
    meta: [
      { title: META_TITLE },
      {
        name: "description",
        content:
          META_DESCRIPTION,
      },
      { property: "og:title", content: META_TITLE },
      {
        property: "og:description",
        content:
          META_DESCRIPTION,
      },
      { property: "og:url", content: canonicalUrl("/meal-plans") },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: META_TITLE },
      {
        name: "twitter:description",
        content:
          META_DESCRIPTION,
      },
    ],
    links: [{ rel: "canonical", href: canonicalUrl("/meal-plans") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: MealPlansPage,
});

const STEPS: Step[] = [
  { title: "Choose your slot", body: "Pick breakfast, lunch, snacks or dinner — whichever fits your day." },
  { title: "Order ahead", body: "Place your order before the slot cutoff so we can plan fresh." },
  { title: "We cook fresh", body: "Meals are prepared to the slot in hygienic, separated kitchens." },
  { title: "Doorstep delivery", body: "Delivered fresh in food-grade packaging across Mumbai." },
];

const FAQ = [
  {
    q: "What meal timings do you offer?",
    a: "Four slots daily: Breakfast, Lunch, Snacks and Dinner. Each slot is prepared and dispatched fresh for that window.",
  },
  {
    q: "What are the order cutoff times?",
    a: "Breakfast — order by 8:00 pm the previous day. Lunch — 6:00 am same day. Snacks — 8:30 pm previous day. Dinner — 12:01 pm same day.",
  },
  {
    q: "How do you ensure freshness?",
    a: "Meals are cooked to the slot they're delivered in — never reheated batches. Farm-direct ingredients and short cook-to-delivery windows keep meals fresh.",
  },
  {
    q: "What kind of packaging do you use?",
    a: "Food-grade, hygienic packaging designed for safe, fresh, in-city delivery.",
  },
  {
    q: "Which areas do you deliver to?",
    a: "We deliver across 70+ Mumbai neighbourhoods. Reach out via the Contact page for the latest coverage list.",
  },
  {
    q: "Do you share pricing and plan details?",
    a: "Plan and pricing details are shared based on your location and requirements. Please contact us for the latest options.",
  },
];

function MealPlansPage() {
  const { plans } = useMealPlans();
  const { catalog: mp2, isMp2Visible: useMp2 } = usePublicMp2Catalog();
  const cards: MealPlan[] = plans.map((p) => ({
    name: p.title,
    img: p.image,
    alt: p.alt,
    desc: p.description,
    cutoff: p.cutoff,
    enquiryMessage: p.enquiryMessage,
    badge: p.badge,
    badges: p.badges || [],
    buttonText: p.buttonText,
    buttonLink: p.buttonLink,
  }));






  // Pick a representative plan/combo/pref/rule/area for the Sample Weekly Menu.
  const sampleSeed = (() => {
    if (!useMp2 || !mp2 || mp2.plans.length === 0) return null;
    const plan = mp2.plans.find((p) => !p.health_review_required) ?? mp2.plans[0];
    if (!plan) return null;

    const combo =
      mp2.combinations.find((c) =>
        !plan.allowed_combination_ids ||
        plan.allowed_combination_ids.length === 0 ||
        plan.allowed_combination_ids.includes(c.id),
      ) ?? mp2.combinations[0];

    const pref =
      mp2.food_preferences.find((p) =>
        !plan.allowed_preference_codes ||
        plan.allowed_preference_codes.length === 0 ||
        plan.allowed_preference_codes.includes(p.code),
      ) ?? mp2.food_preferences[0];

    const rule =
      mp2.delivery_rules.find((r) =>
        !plan.allowed_delivery_rule_ids ||
        plan.allowed_delivery_rule_ids.length === 0 ||
        plan.allowed_delivery_rule_ids.includes(r.id),
      ) ?? mp2.delivery_rules[0];

    const areaIds = new Set(
      mp2.rule_areas.filter((ra) => ra.rule_id === rule?.id).map((ra) => ra.area_id),
    );
    const area = mp2.delivery_areas.find((a) => areaIds.has(a.id));

    if (!combo || !pref || !rule || !area) return null;

    const startDate = "2026-08-10"; // FIXED for demo preview to match existing MP2 scheduled data (Aug 10-16)
    return { plan, combo, pref, rule, area, startDate };
  })();

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Meal Plans"
        title={useMp2 ? "Subscription meal plans, chef-crafted for your day." : "Four meal slots. Fresh variety throughout the day."}
        intro={useMp2 ? "Pick a plan, tell us how often you want deliveries, and pay securely — all in a few clicks." : "Breakfast, Lunch, Snacks and Dinner — each slot is chef-curated for balance, taste and variety, with clear order-ahead cutoffs so your day stays smooth."}
      />

      {/* Intro */}
      <Section>
        <div className="max-w-3xl">
          <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{useMp2 ? "Our Plans" : "Our Categories"}</span>
          <h2 className="mt-3 font-display text-3xl font-medium md:text-4xl">
            {useMp2 ? "Choose the plan that fits your goals." : "Four meal categories, every day."}
          </h2>
          <p className="mt-5 leading-relaxed text-muted-foreground">
            {useMp2
              ? "Every plan is chef-crafted for balanced nutrition and taste, prepared fresh and delivered to your doorstep across Mumbai."
              : "Our menu is built around four daily meal slots — designed to give you nutrition-forward variety without repetition. Pick the slots that fit your day and enquire for details."}
          </p>
          <p className="mt-4 leading-relaxed text-muted-foreground">
            Looking for a tiffin service in Mumbai? Explore our chef-curated meal plans and{" "}
            <Link to="/weekly-menu" className="text-primary underline underline-offset-4">weekly menu</Link>{" "}
            to see the meals available. Delivery options depend on your neighbourhood and selected plan.
          </p>
        </div>

        {useMp2 ? (
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {mp2!.plans.map((p) => (
              <PlanCard key={p.id} plan={p} />
            ))}
          </div>
        ) : cards.length === 0 ? (
          <p className="mt-14 text-sm text-muted-foreground">
            Meal plans are being updated. Please check back soon or reach out via the Contact page.
          </p>
        ) : (
          <div className="mt-14 grid gap-6 sm:grid-cols-2">
            {cards.map((p, i) => (
              <MealPlanCard key={`${p.name}-${i}`} meal={p} />
            ))}
          </div>
        )}
      </Section>

      {useMp2 ? (
        <>
          {/* Typical Dishes — reuses the sample-dishes strip from the homepage. */}
          <SampleDishes />

          {/* Sample Weekly Menu — 5-day illustrative preview */}
          <Section className="bg-secondary/30">
            <div className="max-w-2xl">
              <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Sample weekly menu
              </span>
              <h2 className="mt-3 font-display text-4xl font-medium md:text-5xl">
                A week of chef-curated meals.
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Illustrative sample only — your actual menu is finalised after
                you complete configuration for your plan and neighbourhood.
              </p>
            </div>
            <div className="mt-8">
              {sampleSeed ? (
                <UpcomingMenuPreview
                  planId={sampleSeed.plan.id}
                  combinationId={sampleSeed.combo.id}
                  foodPreferenceCode={sampleSeed.pref.code}
                  deliveryRuleId={sampleSeed.rule.id}
                  deliveryAreaId={sampleSeed.area.id}
                  startDate={sampleSeed.startDate}
                  days={5}
                  headline="Sample week"
                  compact
                  planSlug={sampleSeed.plan.slug}
                  analyticsSurface="meal-plans-index"
                />
              ) : (
                <Alert>
                  <AlertTitle>Sample menu not available yet</AlertTitle>
                  <AlertDescription>
                    We publish menus a few days ahead. Configure a plan to see
                    options for your area.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </Section>
        </>
      ) : null}

      {/* How it works */}
      <Section className="bg-secondary/30">
        <div className="max-w-2xl">
          <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">How Ordering Works</span>
          <h2 className="mt-3 font-display text-4xl font-medium md:text-5xl">Four simple steps.</h2>
        </div>
        <StepList steps={STEPS} />
      </Section>

      <AlacarteComingSoon />

      {/* Delivery coverage */}
      <Section>
        <div className="grid gap-10 md:grid-cols-[1fr_1.2fr] md:items-center md:gap-16">
          <div>
            <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Delivery</span>
            <h2 className="mt-3 font-display text-4xl font-medium md:text-5xl">Across Mumbai.</h2>
          </div>
          <div>
            <p className="text-base leading-relaxed text-muted-foreground">
              Our healthy meal delivery in Mumbai serves selected neighbourhoods, including Andheri,
              Bandra, BKC, Juhu, Powai and Worli. Contact us to confirm delivery to your exact location.
            </p>
            <Link
              to="/contact"
              onClick={() =>
                trackCtaClick({ action: "contact", location: "meal-plans-coverage", href: "/contact" })
              }
              className="mt-6 inline-flex items-center rounded-full border border-border bg-background px-6 py-3 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
            >
              Check your area →
            </Link>
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section className="bg-secondary/30">
        <div className="grid gap-12 md:grid-cols-[1fr_1.5fr] md:gap-20">
          <div>
            <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">FAQ</span>
            <h2 className="mt-3 font-display text-4xl font-medium md:text-5xl">Good questions, honest answers.</h2>
            <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
              Read our <Link to="/faq" className="text-primary underline underline-offset-4">meal and delivery FAQs</Link>{" "}
              or <Link to="/contact" className="text-primary underline underline-offset-4">contact the team</Link>{" "}
              if you have questions before choosing a plan.
            </p>
          </div>
          <Accordion type="single" collapsible className="w-full space-y-2">
            {FAQ.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="rounded-2xl border border-border bg-card px-5 transition-colors data-[state=open]:border-primary/30"
              >
                <AccordionTrigger className="py-5 text-left font-display text-lg font-medium tracking-tight hover:no-underline md:text-xl">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-sm leading-relaxed text-muted-foreground">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="pb-24">
        <div className="relative overflow-hidden rounded-[2rem] bg-primary px-10 py-20 text-primary-foreground md:px-16 md:py-28">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,color-mix(in_oklab,var(--accent)_35%,transparent),transparent_60%)]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,color-mix(in_oklab,var(--primary-foreground)_10%,transparent),transparent_55%)]"
          />
          <div className="relative max-w-2xl">
            <div className="mb-4 h-px w-10 bg-primary-foreground/50" aria-hidden />
            <span className="text-xs uppercase tracking-[0.2em] text-primary-foreground/80">Get in touch</span>
            <h2 className="mt-4 font-display text-4xl font-medium leading-tight md:text-6xl">
              Ready to order?
            </h2>
            <p className="mt-5 max-w-xl text-primary-foreground/90">
              Message us on WhatsApp for the fastest response, or drop us a note via the Contact page.
            </p>
            <WhatsAppActions
              className="mt-10"
              location="meal-plans-cta"
              tone="dark"
              compact
              chatLabel="Chat on WhatsApp"
            />
            <div className="mt-4">
              <Link
                to="/contact"
                onClick={() =>
                  trackCtaClick({ action: "contact", location: "meal-plans-cta", href: "/contact" })
                }
                className="inline-flex items-center rounded-full border border-primary-foreground/40 bg-primary-foreground/5 px-6 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-foreground/10"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </Section>
    </SiteLayout>
  );
}